import dayjs from 'dayjs';
import dayjsPluginUTC from 'dayjs-plugin-utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import _ from 'lodash';
import validateCardNumber from './validateCardNumber';
import getDuplicateCardMessage from './getDuplicateCardMessage';
import {
  CreditCardsWithAddressErrors,
  CreditCardsErrors,
  CreditCardsFormValidation,
  CreditCardCodes,
  PaymentModes,
} from '../../constants';

dayjs.extend(customParseFormat);
dayjs.extend(dayjsPluginUTC);

const validateCreditCards = (
  isCardAddressRequired,
  isCardSecurityCodeRequired,
  paymentMethods,
  states,
  isBooking,
  acceptedPaymentCards,
  chargeableItemOptions,
  tokenizedData,
) => {
  let isValid = true;
  let isSavedCardValidated = true;

  // #region Saved Card Validation
  const isSavedCardInvalid =
    isBooking &&
    Object.values(paymentMethods || {}).some((pm) => {
      if (pm.paymentMode !== PaymentModes.SAVED_CARD) return false;
      const acceptedCodes = Object.keys(acceptedPaymentCards || {});
      return (
        !pm.selectedCard ||
        !pm.selectedCard.id ||
        pm.selectedCard.isExpired ||
        (acceptedCodes.length > 0 && !acceptedCodes.includes(pm.selectedCard.cardCode))
      );
    });
  if (isSavedCardInvalid) {
    isValid = false;
    isSavedCardValidated = false;
  }

  // The same saved card picked for two payment methods; flag every pick after the first.
  const seenSavedCardIds = new Set();
  const duplicateSavedCardEntries = isBooking
    ? Object.entries(paymentMethods || {}).filter(([, pm]) => {
        const savedCardId = pm?.paymentMode === PaymentModes.SAVED_CARD && pm.selectedCard?.id;
        if (!savedCardId) return false;
        if (seenSavedCardIds.has(savedCardId)) return true;
        seenSavedCardIds.add(savedCardId);
        return false;
      })
    : [];
  if (duplicateSavedCardEntries.length) {
    return [
      false,
      duplicateSavedCardEntries.map(([index, pm]) => ({
        cardIndex: Number(index),
        isValid: false,
        cardformValidations: {
          ...CreditCardsFormValidation,
          ...(pm.formValidations || {}),
          duplicateCardNumber: getDuplicateCardMessage(pm.selectedCard.last4digits),
        },
      })),
      isSavedCardValidated,
    ];
  }
  // #endregion

  const creditCardsFormValidations = [];
  let cardIndexForError = 0;
  let errorMessage = null;

  // Check if all chargeable items are covered by one card when multiple cards are present
  if (
    isBooking &&
    chargeableItemOptions &&
    paymentMethods &&
    Object.values(paymentMethods).length > 1 &&
    chargeableItemOptions.length > 0
  ) {
    const allSelectedItems = _.filter(
      _.flatMap(Object.values(paymentMethods), (pm) => pm.selectedFor),
      (item) => item !== undefined && item !== null,
    );
    if (_.uniq(allSelectedItems).length !== allSelectedItems.length) {
      const duplicateItems = _.findLast(_.groupBy(allSelectedItems), (e) => e && e.length > 1);
      if (duplicateItems && duplicateItems.length > 0) {
        errorMessage = `${_.find(chargeableItemOptions, (item) => item.value === duplicateItems[0])?.label || 'Chargeable'} is already assigned to another card`;
        cardIndexForError = Object.values(paymentMethods).findLastIndex(
          (card) => card?.selectedFor && card?.selectedFor.includes(duplicateItems[0]),
        );
      }
      isValid = false;
    } else {
      // Check each chargeable item is covered - with null checks
      const missingItems = _.map(
        _.filter(
          chargeableItemOptions,
          (item) => item && item.value && !allSelectedItems.includes(item.value),
        ),
        'label',
      );
      if (missingItems.length > 0) {
        isValid = false;
        errorMessage =
          missingItems.length === 1
            ? `The following item is not assigned to any card: ${missingItems[0]}`
            : `The following items are not assigned to any card: ${missingItems.join(', ')}`;
        // Find the card with the most available capacity to show the error - with null checks
        cardIndexForError = Object.entries(paymentMethods).length - 1;
        let maxAvailableSpace = -1;
        const totalItems = chargeableItemOptions.length;

        Object.entries(paymentMethods).forEach(([index, card]) => {
          if (card) {
            const currentSelectedCount = (card?.selectedFor && card?.selectedFor?.length) || 0;
            const availableSpace = totalItems - currentSelectedCount;

            if (availableSpace > maxAvailableSpace) {
              maxAvailableSpace = availableSpace;
              cardIndexForError = Number(index);
            }
          }
        });
      }
    }

    if (!isValid) {
      // Apply the error only to the selected card
      if (paymentMethods[cardIndexForError]) {
        const bestCard = paymentMethods[cardIndexForError];
        const newFormValidations = bestCard.formValidations ? { ...bestCard.formValidations } : {};

        newFormValidations.selectedFor = errorMessage;

        creditCardsFormValidations.push({
          cardIndex: Number(cardIndexForError),
          isValid: false,
          cardformValidations: newFormValidations,
        });
      }

      // Add all other cards without this error
      Object.entries(paymentMethods).forEach(([index, card]) => {
        if (Number(index) !== cardIndexForError && card) {
          const formValidations = card.formValidations ? { ...card.formValidations } : {};

          creditCardsFormValidations.push({
            cardIndex: Number(index),
            isValid: Object.keys(formValidations).length === 0,
            cardformValidations: formValidations,
          });
        }
      });
      return [isValid, creditCardsFormValidations, isSavedCardValidated];
    }
  }

  if (tokenizedData) {
    return [isValid, creditCardsFormValidations, isSavedCardValidated];
  }

  const seenCardNumbers = new Set(); // Track unique card numbers
  // Validate each credit card
  Object.entries(paymentMethods).forEach(([index, { creditCard }]) => {
    const paymentMethod = paymentMethods[index];

    const newFormValidations = { ...paymentMethod.formValidations };
    const cardCode = CreditCardCodes[creditCard.component];
    const acceptedCardCodes = Object.keys(acceptedPaymentCards || {});
    const acceptedCardValues = Object.values(acceptedPaymentCards || {});
    const cardNumber = creditCard.cardNumber?.replace(/\s+/g, '');

    // Check if the card is selected for chargeable items
    if (
      isBooking &&
      chargeableItemOptions &&
      chargeableItemOptions.length > 0 &&
      Object.values(paymentMethods).length > 1 &&
      (!paymentMethod?.selectedFor || paymentMethod?.selectedFor?.length === 0)
    ) {
      newFormValidations.selectedFor =
        'Please assign at least one chargeable option to this card, or remove the credit card to continue';
      isValid = false;
    }

    if (isBooking && paymentMethod.paymentMode === PaymentModes.NEW_CREDIT_CARD) {
      Object.entries(
        isCardAddressRequired ? CreditCardsWithAddressErrors : CreditCardsErrors,
      ).forEach(([key, value]) => {
        if ((key !== 'cvc' || isCardSecurityCodeRequired) && !creditCard[key]) {
          newFormValidations[key] = value;
          isValid = false;
        }
      });

      if (
        dayjs(new Date()).diff(dayjs(creditCard.expiration, 'MM/YY'), 'month') > 0 ||
        Number(creditCard.expiration.slice(0, 2)) !==
          dayjs(creditCard.expiration, 'MM/YY').get('month') + 1
      ) {
        newFormValidations.expiration = 'Please use a valid credit card (expiration date)';
        isValid = false;
      }

      if (
        creditCard.cardNumber.length === 0 ||
        creditCard.validCardNumberLengths.every(
          (validLength) => validLength !== creditCard.cardNumber.length,
        ) ||
        !validateCardNumber(creditCard.cardNumber) ||
        creditCard.component === 'Empty'
      ) {
        newFormValidations.cardNumber = 'Please use a valid credit card (card number)';
        isValid = false;
      }

      if (
        isCardSecurityCodeRequired &&
        (creditCard.cvc.length === 0 || creditCard.cvc.length !== creditCard.validCvcLength)
      ) {
        newFormValidations.cvc = 'Please use a valid credit card (Security code (cvc))';
        isValid = false;
      }

      if (isCardAddressRequired && states && states.length && !creditCard.state) {
        newFormValidations.state = 'State is required';
        isValid = false;
      }

      // Check if the card is accepted by the hotel
      if (
        acceptedCardCodes &&
        acceptedCardCodes.length > 0 &&
        !acceptedCardCodes.includes(cardCode)
      ) {
        newFormValidations.cardNumber = `${creditCard.component} card isn't accepted by the hotel. Please use one of these cards: ${acceptedCardValues.join(', ')}`;
        isValid = false;
      }

      // Check if the card number is already exists
      if (cardNumber) {
        if (seenCardNumbers.has(cardNumber)) {
          newFormValidations.cardNumber = getDuplicateCardMessage(cardNumber.slice(-4));
          isValid = false;
        } else {
          seenCardNumbers.add(cardNumber); // Add to tracked numbers
        }
      }
    }

    const isValidEntry =
      paymentMethod.paymentMode === PaymentModes.CASH ||
      Object.keys(newFormValidations ?? {}).length === 0 ||
      Object.values(newFormValidations ?? {}).every(
        (value) => value === null || value === undefined,
      );

    creditCardsFormValidations.push({
      cardIndex: Number(index),
      isValid: isValidEntry,
      cardformValidations: newFormValidations,
    });
  });
  return [isValid, creditCardsFormValidations, isSavedCardValidated];
};

export default validateCreditCards;
